// ---------------------------------------------------------------------------
// SINGLE SOURCE OF TRUTH for every pricing-page NUMBER.
//
// Tier prices, per-tier photo storage, embed-video minutes, the video every studio
// starts with, the client video-delivery packs and their refund window, and the
// storage / gallery-access windows all live here, and so do the payment processing
// rates. Edit this file to change what the page shows — nothing is hardcoded in the
// markup. These values are rendered by:
//   • components/Pricing.jsx            (the tier cards)
//   • components/ProcessingRateInfo.jsx (the card and bank payment fee notes)
//   • components/PricingDetails.jsx     (storage + video + differentiators)
//   • config/planComparison.js          (the full comparison matrix)
//   • pages/GalleryDeliveryPage.jsx     (the video line on the gallery delivery page)
//
// NOTE: the founding-member PROMO math (the launch discount %, its duration,
// and the annual "2 months free" cadence) lives separately in
// config/pricingPromo.js — that file is the single source for the promo, this
// one is the single source for the plan numbers. Keep them separate.
//
// Currency: video pack prices are ruled separately in each PAYMENT_COUNTRIES
// currency, and a visitor is offered them only in a launched market (PACK_MARKETS).
// Plans and gallery extensions have no ruled price outside USD and must not be
// shown in another currency. See videoDeliveryPacks.
// ---------------------------------------------------------------------------

import { LAUNCH_TERRITORIES } from './territories';

// Tier order, left → right, as shown on the page.
export const TIER_ORDER = ['free', 'basic', 'pro', 'studio'];

// Canonical MONTHLY list price (USD) before billing cadence or the
// founding-member promo. Free has no price. In annual mode the cards show the
// "2 months free" per-month equivalent (see pricingPromo.computeDisplayedListMonthly),
// which is why $39 / $69 / $119 render as $33 / $58 / $99 on the annual toggle.
export const tierListMonthly = {
  free: 0,
  basic: 39,
  pro: 69,
  studio: 119,
};

// Photo storage included with each plan.
//   • Storage / hosting is a PAID feature — Free gets none (null).
//   • Need more? You upgrade your plan (there is no photo-storage add-on).
//   • Values are display strings so "Unlimited" renders literally.
export const tierPhotoStorage = {
  free: null, // null → not included (storage is a paid feature)
  basic: '200 GB',
  pro: '1 TB',
  studio: 'Unlimited',
};

// Brands (separate studio identities) included with each plan. Every tier gets at
// least one, so this is never "not included": the difference is how many. Display
// strings so "Unlimited" renders literally, matching tierPhotoStorage.
export const tierBrands = {
  free: '1 brand',
  basic: '1 brand',
  pro: '2 brands',
  studio: 'Unlimited brands',
};

// ---------------------------------------------------------------------------
// Payment processing.
//
// Pixally charges Stripe's standard rate in the photographer's own country plus a
// margin set by the plan. So the rates below are Stripe's, and each plan says what
// it adds. Change a Stripe rate or a plan's terms here and the tier cards, their
// fee notes (components/ProcessingRateInfo.jsx) and the comparison table all follow,
// down to the worked examples in the bank payments note. These rates once lived as
// literals in both the cards and the table, which is how the two came to disagree.
//
// Percentages are basis points (290 is 2.9%) and money is minor units (30 is 30¢),
// so a margin adds exactly: 1.7% plus 0.5% is 2.2%, never 2.2000000000000002%.
// ---------------------------------------------------------------------------

// The countries the fee notes cover, in the order they list them. The tier cards
// and the comparison table quote the first.
export const PAYMENT_COUNTRIES = [
  { code: 'US', name: 'United States', currency: 'USD', stripePricingUrl: 'https://stripe.com/pricing' },
  { code: 'CA', name: 'Canada', currency: 'CAD', stripePricingUrl: 'https://stripe.com/en-ca/pricing' },
  { code: 'GB', name: 'UK', currency: 'GBP', stripePricingUrl: 'https://stripe.com/gb/pricing' },
  { code: 'AU', name: 'Australia', currency: 'AUD', stripePricingUrl: 'https://stripe.com/au/pricing' },
];

export const QUOTED_PAYMENT_COUNTRY = PAYMENT_COUNTRIES[0];

// A fee under one whole unit reads the way people say it, 30¢ or 20p. Canadian and
// Australian dollars keep the symbol instead: CA$0.30, A$0.30.
//
// `spoken` is what a screen reader says after a currency switch, because "CA$" read
// aloud is letters. `unitPriceInSubunit` is how each market writes a price per GB:
// a UK shopper reads "79p per GB", a US one reads "$0.95 per GB" rather than
// "95¢". That is the ruled per-GB table, not a formatting preference.
export const CURRENCIES = {
  USD: { symbol: '$', subunit: '¢', spoken: 'US dollars', unitPriceInSubunit: false },
  CAD: { symbol: 'CA$', spoken: 'Canadian dollars', unitPriceInSubunit: false },
  GBP: { symbol: '£', subunit: 'p', spoken: 'pounds', unitPriceInSubunit: true },
  AUD: { symbol: 'A$', spoken: 'Australian dollars', unitPriceInSubunit: false },
};

// Every country outside PACK_MARKETS, and every failure to read a country, sees
// USD. Never inferred from language, timezone or browser locale: a Canadian with an
// en-GB browser is not a British customer.
export const DEFAULT_CURRENCY = 'USD';

const isCurrency = (code) => Object.prototype.hasOwnProperty.call(CURRENCIES, code);

// Stripe's standard rates by country: `card` for domestic cards, and `bank` for a
// bank payment, with the fixed fee added on top or the minimum charged, where there
// is one. Stripe lowers Australia's domestic card rate from 1 Oct 2026
// (stripe.com/au/pricing), and every Australian card figure on the page follows this
// one number.
const STRIPE_STANDARD_RATES = {
  US: { card: { bps: 290, fixed: 30 }, bank: { bps: 80 } },
  CA: { card: { bps: 290, fixed: 30 }, bank: { bps: 100, fixed: 40 } },
  GB: { card: { bps: 150, fixed: 20 }, bank: { bps: 100, minimum: 20 } },
  AU: { card: { bps: 170, fixed: 30 }, bank: { bps: 100, fixed: 30 } },
};

// What each plan adds to Stripe's rates, and the most one bank payment can cost, in
// whole units of the photographer's own currency: 12 is $12, CA$12, £12 or A$12.
// Free and Basic share one bank rate, and Pro and Studio share the other.
export const tierPaymentTerms = {
  free: { cardMarginBps: 50, bankMarginBps: 20, bankCap: null },
  basic: { cardMarginBps: 0, bankMarginBps: 20, bankCap: null },
  pro: { cardMarginBps: 0, bankMarginBps: 0, bankCap: 12 },
  studio: { cardMarginBps: 0, bankMarginBps: 0, bankCap: 8 },
};

// The payment the bank payments note prices on plans with no cap, in whole units
// of each country's currency.
export const BANK_EXAMPLE_PAYMENT = 3000;

const currencyOf = (countryCode) =>
  PAYMENT_COUNTRIES.find((country) => country.code === countryCode).currency;

// "2.9%"
export const formatPercent = (bps) => `${bps / 100}%`;

// "$3,000", "CA$36.40", "£36"
export const formatMoney = (minorUnits, currency) => {
  const major = minorUnits / 100;
  const digits = Number.isInteger(major) ? 0 : 2;
  const amount = major.toLocaleString('en-US', {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  });
  return `${CURRENCIES[currency].symbol}${amount}`;
};

// "30¢", "20p", "CA$0.40"
const formatFee = (minorUnits, currency) => {
  const { subunit } = CURRENCIES[currency];
  return subunit && minorUnits < 100 ? `${minorUnits}${subunit}` : formatMoney(minorUnits, currency);
};

const cardRate = (tier, countryCode) => {
  const { bps, fixed } = STRIPE_STANDARD_RATES[countryCode].card;
  return { bps: bps + tierPaymentTerms[tier].cardMarginBps, fixed };
};

const bankRate = (tier, countryCode) => {
  const rate = STRIPE_STANDARD_RATES[countryCode].bank;
  return { ...rate, bps: rate.bps + tierPaymentTerms[tier].bankMarginBps };
};

// "3.4% + 30¢", "2.2% + A$0.30"
export const cardRateLabel = (tier, countryCode) => {
  const { bps, fixed } = cardRate(tier, countryCode);
  return `${formatPercent(bps)} + ${formatFee(fixed, currencyOf(countryCode))}`;
};

// "0.8%", "1.2% + CA$0.40". A minimum is not part of the rate; see bankMinimumLabel.
export const bankRateLabel = (tier, countryCode) => {
  const { bps, fixed } = bankRate(tier, countryCode);
  return fixed
    ? `${formatPercent(bps)} + ${formatFee(fixed, currencyOf(countryCode))}`
    : formatPercent(bps);
};

// "20p", or null where there is no minimum.
export const bankMinimumLabel = (tier, countryCode) => {
  const { minimum } = bankRate(tier, countryCode);
  return minimum ? formatFee(minimum, currencyOf(countryCode)) : null;
};

// The fee on one bank payment, in minor units: the rate and fixed fee, at least the
// minimum, and never more than the plan's cap.
export const bankFeeOn = (tier, countryCode, paymentMinorUnits) => {
  const { bps, fixed = 0, minimum = 0 } = bankRate(tier, countryCode);
  const { bankCap } = tierPaymentTerms[tier];
  const fee = Math.max(Math.round((paymentMinorUnits * bps) / 10000) + fixed, minimum);
  return bankCap === null ? fee : Math.min(fee, bankCap * 100);
};

const byTier = (label) => Object.fromEntries(TIER_ORDER.map((tier) => [tier, label(tier)]));

// What the tier cards and the comparison table print for the quoted country:
// "2.9% + 30¢" and "0.8%, max $12".
export const tierCardRate = byTier((tier) => cardRateLabel(tier, QUOTED_PAYMENT_COUNTRY.code));

export const tierBankRate = byTier((tier) => {
  const { bankCap } = tierPaymentTerms[tier];
  const cap =
    bankCap === null ? 'no cap' : `max ${formatMoney(bankCap * 100, QUOTED_PAYMENT_COUNTRY.currency)}`;
  return `${bankRateLabel(tier, QUOTED_PAYMENT_COUNTRY.code)}, ${cap}`;
});

// EMBED video hosting included with each paid plan, in minutes.
// This is film you embed on your OWN website — no expiration, no renewal.
// It is NOT the same thing as client video delivery (below).
export const tierEmbedVideoMinutes = {
  free: 0,
  basic: 60,
  pro: 90,
  studio: 120,
};

// Website videos on the FREE plan (Ryan, 2026-10-04: FREE VIDEOS, FREE COSTS).
// A Free studio may put up to this many videos on its own website, each within
// these limits. Delivering films to clients still needs a paid plan. Free has no
// minutes pool, so tierEmbedVideoMinutes.free stays 0.
// ⛔ Ryan, 2026-10-06: the Pixally logo in the website video player is NOT
// mentioned anywhere on the pricing page. The "Your brand, never ours" banner and
// the "No 'Powered by Pixally' branding, ever" row stay as they are.
export const freeWebsiteVideos = { count: 3, maxMinutes: 10, maxGB: 2 };

// Client VIDEO DELIVERY packs — pay-per-upload, account-wide, and they roll
// across all your projects. Available on paid plans. Separate from the embed
// hosting above (that's for your site; this is for delivering films to clients).
//
// ⛔ THE PRICES ON EACH ROW ARE RULED INDEPENDENTLY AND ARE NEVER COMPUTED FROM
// EACH OTHER. Each one is a round number chosen to clear a US-dollar cost
// floor after that market's own card and conversion fees, which is why CA$70 is
// not 50 times any rate. There is no exchange rate in this feature: a converted
// price would drift away from the ruled one every day, so the page shows each
// market's own ruled price. Ruled by Ryan, USD on 2026-09-01 and the rest on
// 2026-09-21.
//
// ⛔ WHETHER TAX IS ADDED ON TOP OF THESE PRICES IS NOT DECIDED. Ryan's accountant
// is being asked (2026-09-21). Until it is ruled, no copy may say a pack price is
// what you pay, and none may say tax is included or extra.
//
// Whole units, like the plan prices: 70 is CA$70.
//
// AUD is ruled and DORMANT. Australia is not in LAUNCH_TERRITORIES, so no visitor
// is offered A$ and Australians see USD like any other country we have not launched
// in. Keep these prices: they are the launch prices, and adding 'AU' to
// LAUNCH_TERRITORIES is what turns them on.
export const videoDeliveryPacks = [
  { size: '50 GB', prices: { USD: 50, CAD: 70, GBP: 40, AUD: 80 } },
  { size: '100 GB', prices: { USD: 95, CAD: 135, GBP: 79, AUD: 150 } },
  { size: '500 GB', prices: { USD: 450, CAD: 660, GBP: 385, AUD: 730 } },
];

// The markets a visitor can be shown pack prices for: launched, AND ruled in every
// pack. Derived from the two lists rather than kept as a third, so they cannot
// disagree: the site never quotes a price in a market nobody can sign up from,
// and a newly launched market with no ruled price falls back to USD rather than
// showing a blank one. In PAYMENT_COUNTRIES order, which is the switcher's order.
export const PACK_MARKETS = PAYMENT_COUNTRIES.filter(
  ({ code, currency }) =>
    LAUNCH_TERRITORIES.includes(code) &&
    videoDeliveryPacks.every((pack) => Object.prototype.hasOwnProperty.call(pack.prices, currency)),
);

export const isOfferedPackCurrency = (currency) =>
  PACK_MARKETS.some((market) => market.currency === currency);

// ISO-3166 alpha-2 in, currency out, and USD for any country not in PACK_MARKETS.
// GB, not UK: GB is the code the edge sends.
export const packCurrencyForCountry = (countryCode) =>
  PACK_MARKETS.find((market) => market.code === String(countryCode || '').toUpperCase())
    ?.currency || DEFAULT_CURRENCY;

// The price of one pack in one currency. Falls back to USD for any code that is
// not in CURRENCIES, so an unexpected code can never render "undefined".
const packCurrency = (currency) => (isCurrency(currency) ? currency : DEFAULT_CURRENCY);

export const packPrice = (pack, currency = DEFAULT_CURRENCY) => pack.prices[packCurrency(currency)];

export const packGb = (pack) => parseInt(pack.size, 10);

// "CA$135"
export const packPriceLabel = (pack, currency = DEFAULT_CURRENCY) =>
  formatMoney(packPrice(pack, currency) * 100, packCurrency(currency));

// Derived, never hand-written: the per-GB rate is the whole argument for buying a
// bigger pack, and a hardcoded string would silently start lying the first time a
// price above changes. This is division of one ruled price by its own GB, not a
// conversion between currencies, and it is done in minor units so it is exact:
// 7900p over 100 GB is 79p, with no float to round. Every ruled price divides into
// whole cents or pence, and a new price that does not is refused here rather than
// rounded, because a rounded rate times the GB would no longer be the ruled price.
export const pricePerGbMinor = (pack, currency = DEFAULT_CURRENCY) => {
  const perGb = (packPrice(pack, currency) * 100) / packGb(pack);
  if (!Number.isInteger(perGb)) {
    throw new Error(`${pack.size} in ${currency} is not a whole number of minor units per GB`);
  }
  return perGb;
};

// "$1.00 per GB", "CA$1.32 per GB", "79p per GB". Always to the cent, never
// "$1", because the column is read down as a comparison.
export const pricePerGbLabel = (pack, currency = DEFAULT_CURRENCY) => {
  const code = packCurrency(currency);
  const minor = pricePerGbMinor(pack, code);
  const { symbol, subunit, unitPriceInSubunit } = CURRENCIES[code];
  const rate = unitPriceInSubunit ? `${minor}${subunit}` : `${symbol}${(minor / 100).toFixed(2)}`;
  return `${rate} per GB`;
};

// The video every studio starts with, in GB. It is given ONCE, when a studio first
// pays for a plan, on top of anything uploaded during the free trial, which stays
// free. After that, more video comes from packs. Ruled by Ryan on 2026-09-24.
// ⛔ ONCE, not every year. Never word it as a yearly or monthly allowance, and never
// as "included video" without saying it is a starting amount.
export const STARTER_VIDEO_GB = 50;

// A pack can be refunded within this many days of buying it, as long as the studio's
// unused GB are at least that pack's full size. GB are pooled, so there is no record
// of which pack an upload drew from, and the test never needs one. Ruled by Ryan on
// 2026-09-24.
export const PACK_REFUND_DAYS = 14;

// The handful of delivery rules that change a purchase decision, so nobody
// meets them for the first time in a support ticket. Deliberately short: the
// full policy lives in help, and the internal economics behind these rules
// (vendor costs, per-minute margins, pricing still under review) are NOT
// public and must not migrate into this file.
export const videoDeliveryPolicy = [
  {
    title: `Every studio starts with ${STARTER_VIDEO_GB} GB`,
    detail: `When your studio first pays for a plan, it gets ${STARTER_VIDEO_GB} GB of video, once. That is on top of anything you uploaded during your free trial, which stays free. After that, more video comes from packs.`,
  },
  {
    title: 'GB are spent when you upload',
    detail:
      'A film costs its file size in GB the moment it uploads, and every brand in your studio shares the same GB. Packs never expire, so anything you do not use stays there for the next wedding.',
  },
  {
    title: `${PACK_REFUND_DAYS} days to refund a pack`,
    detail: `A pack can be refunded within ${PACK_REFUND_DAYS} days of buying it, as long as you still have at least as many unused GB as the pack holds.`,
  },
  {
    // ⛔ AN UNDELIVERED FILM'S GB DO NOT COME BACK. This rule used to promise they
    // returned automatically at 30 days; Ryan cancelled that on 2026-09-01. GB never
    // expire, and the two-year window only starts at delivery, so returning the GB of
    // a film that was never delivered would invite parking films for free.
    title: 'Upload means you are delivering',
    detail:
      'A film that is never delivered is removed 30 days after it uploads. Its GB stay used. We remind you before it is removed. If a film is not ready to deliver, wait to upload it. Deleting a film yourself does not return its GB either.',
  },
  {
    title: 'Revisions are free until you deliver, plus 10 days',
    detail:
      'Replace a film as many times as you like before it goes out and for 10 days after. A larger replacement charges only the difference in GB. After those 10 days, a revision is a new upload.',
  },
  {
    title: 'One date per gallery',
    detail:
      'The two years starts when a gallery is first delivered. A film you add to that gallery later inherits its date rather than starting a new clock, so the couple only ever sees one date. Deliver a late film as its own gallery if you want it to have its own full window.',
  },
  {
    // Reframed from the limitation ("the original goes away") to the reason it
    // exists. Warehousing every master forever is what makes archive services
    // expensive, and not doing it is precisely why a pack costs $50 instead of
    // what cold storage would force. That is a selling point, not an apology.
    title: 'Downloads in 4K, for the whole window',
    detail:
      'Your couple streams and downloads the full 4K film for as long as the gallery is live, with no per-view fees and no download caps. The camera original is available too for the first 90 days after delivery, which is when couples actually ask for it, and one checkbox keeps a film\'s original for the full window if you want it there. Not warehousing every master forever is exactly why packs cost what they do.',
  },
];

// Typical delivery size for one wedding: a highlight film plus a full
// documentary edit. Used to turn the pack table above into a per-wedding cost
// on the pricing page, because "50 GB for $50" means nothing to a videographer
// until it is expressed as weddings.
export const AVG_WEDDING_DELIVERY_GB = 15;

// Derived from videoDeliveryPacks on purpose: change a pack price above and the
// worked example moves with it, so the two can never disagree on the page.
// `perWeddingLabel` is "$15" or "£11.85": the per-GB rate times the GB, so it
// stays exact in every currency.
export const weddingDeliveryCost = (
  gbPerWedding = AVG_WEDDING_DELIVERY_GB,
  currency = DEFAULT_CURRENCY,
) =>
  videoDeliveryPacks.map((pack) => ({
    size: pack.size,
    weddings: Math.floor(packGb(pack) / gbPerWedding),
    perWeddingLabel: formatMoney(pricePerGbMinor(pack, currency) * gbPerWedding, packCurrency(currency)),
  }));

// How long a delivered gallery stays live for the client.
//   • Delivered VIDEO stays live for `deliveredVideoYears`, and can be
//     extended anytime to keep it longer.
//   • PHOTOS in a delivered gallery are kept for the life of the subscription
//     (a plain flat cap — no rolling meter, no auto-archiving).
export const accessWindows = {
  deliveredVideoYears: 2,
  videoExtendable: true,
  photosForLifeOfSubscription: true,
};

// Real edges over standalone gallery tools (Pic-Time / Pixieset / VidFlow).
// `icon` is a string key mapped to a lucide icon inside PricingDetails.jsx so
// this config stays pure data.
export const differentiators = [
  {
    icon: 'film',
    title: 'No film length limits',
    detail:
      'Deliver a three-hour documentary edit or a 90-second teaser. We never cap your runtime. Most gallery tools stop feature films at 90 minutes.',
  },
  {
    icon: 'workflow',
    title: 'Delivery tied to the booking',
    detail:
      'The same client record flows from first inquiry to signed contract to the delivered gallery. Nothing gets re-entered, exported between tools, or lost in the gaps.',
  },
  {
    icon: 'infinity',
    title: 'Showreel time that doesn’t expire',
    detail:
      `Host your films on your own website with no expiry date and no renewal fees. They stay live for the life of your subscription: Showreel time on every paid plan, and up to ${freeWebsiteVideos.count} films on Free.`,
  },
  {
    // ⛔ THIS CARD USED TO SAY "as many times as you want, with no deadline".
    // That is false. Per the video model, replacing a film is free before its
    // first delivery and for 10 DAYS AFTER; past that a revision meters as a new
    // upload. Do not restore the unlimited framing. The honest version is still a
    // strong card: unlimited swaps while you iterate, which is when re-cuts
    // actually happen, and a bigger file only ever costs the GB difference.
    icon: 'revise',
    title: 'Free revisions while you iterate',
    detail:
      'Swap a film as many times as you like before it goes out, and for 10 days after delivery. Re-cut, re-color, fix a name in the titles: none of it costs you a second upload. Even a longer version only charges the difference in size. After those 10 days, a new version counts as a new upload.',
  },
];

// ---------------------------------------------------------------------------
// Small formatting helpers so the window / pack numbers are derived in one
// place and never re-typed in copy.
// ---------------------------------------------------------------------------

// "2 years, extendable" (or "2 years" if extension is ever turned off).
// ⛔ "2 years" alone is not true and must not ship without "from delivery".
// The clock starts when a gallery is FIRST DELIVERED, not when a film is
// uploaded, and a film added to an already-delivered gallery inherits that
// gallery's existing date rather than starting its own two years. So a real
// film can be hosted for well under two years. Saying it plainly here is worth
// more than the extra three words cost us.
export const deliveredVideoAccessLabel = () => {
  const y = accessWindows.deliveredVideoYears;
  const base = `${y} year${y === 1 ? '' : 's'} from delivery`;
  return accessWindows.videoExtendable ? `${base}, extendable` : base;
};

// "50 GB for $50, 100 GB for $95, 500 GB for $450"
export const videoDeliveryPacksSummary = (currency = DEFAULT_CURRENCY) =>
  videoDeliveryPacks.map((p) => `${p.size} for ${packPriceLabel(p, currency)}`).join(', ');
